import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api';
import { Button, Input, Alert, Card } from '../components/ui';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const requestCode = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/api/auth/password-reset/request', { email }, { signal: AbortSignal.timeout(70_000) });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not send the code. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const confirmReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await api.post('/api/auth/password-reset/confirm', { email, code, newPassword });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reset failed.');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setResending(true);
    setError('');
    try {
      await api.post('/api/auth/password-reset/request', { email }, { signal: AbortSignal.timeout(70_000) });
    } catch (err) {
      setError(err instanceof ApiError ? err.friendlyMessage : 'Could not resend the code.');
    } finally {
      setResending(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="font-heading text-xl font-bold text-ink mb-2">Password updated</h2>
          <Link to="/login" className="text-sand-strong font-medium hover:underline">Log in</Link>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center px-4">
        <Card className="w-full max-w-sm p-8">
          <h1 className="font-heading text-xl font-bold text-ink mb-1">Check your email</h1>
          <p className="text-sm text-ink-soft mb-6">
            If that address is registered, a 6-digit code was sent. Check spam/junk if it is not in the inbox, then enter it below.
          </p>
          {error && <Alert type="error" className="mb-4">{error}</Alert>}
          <form onSubmit={confirmReset} className="flex flex-col gap-4">
            <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            <Input
              label="6-digit code"
              value={code}
              onChange={e => setCode(e.target.value)}
              maxLength={6}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
            />
            <Input
              label="New password"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <Button type="submit" loading={loading} className="w-full">Set new password</Button>
          </form>
          <button
            type="button"
            onClick={resendCode}
            disabled={resending || loading}
            className="w-full mt-3 text-sm text-sand-strong font-medium hover:underline disabled:opacity-50"
          >
            {resending ? 'Sending…' : 'Resend code'}
          </button>
          <p className="text-center text-sm text-ink-soft mt-6">
            <Link to="/login" className="text-sand-strong font-medium hover:underline">Back to log in</Link>
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <Card className="w-full max-w-sm p-8">
        <h1 className="font-heading text-xl font-bold text-ink mb-1">Forgot password</h1>
        <p className="text-sm text-ink-soft mb-6">Enter your email to receive a reset code.</p>
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        <form onSubmit={requestCode} className="flex flex-col gap-4">
          <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
          <Button type="submit" loading={loading} className="w-full">Send code</Button>
        </form>
        <p className="text-center text-sm text-ink-soft mt-6">
          <Link to="/login" className="text-sand-strong font-medium hover:underline">Back to log in</Link>
        </p>
      </Card>
    </div>
  );
}

export function ResetPasswordPage() {
  const params = new URLSearchParams(window.location.search);
  const [form, setForm] = useState({
    email: params.get('email') ?? '',
    code: '',
    newPassword: '',
  });
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      await api.post('/api/auth/password-reset/confirm', form);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Reset failed.');
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <div className="text-center">
        <h2 className="font-heading text-xl font-bold text-ink mb-2">Password updated</h2>
        <Link to="/login" className="text-sand-strong font-medium hover:underline">Log in</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center px-4">
      <Card className="w-full max-w-sm p-8">
        <h1 className="font-heading text-xl font-bold text-ink mb-1">Reset password</h1>
        <p className="text-sm text-ink-soft mb-6">Enter the code from your email.</p>
        {error && <Alert type="error" className="mb-4">{error}</Alert>}
        <form onSubmit={submit} className="flex flex-col gap-4">
          <Input label="Email" type="email" value={form.email} onChange={set('email')} required />
          <Input label="6-digit code" value={form.code} onChange={set('code')} maxLength={6} placeholder="123456" required />
          <Input label="New password" type="password" value={form.newPassword} onChange={set('newPassword')} required />
          <Button type="submit" loading={loading} className="w-full">Set new password</Button>
        </form>
        <p className="text-center text-sm text-ink-soft mt-6">
          <Link to="/forgot-password" className="text-sand-strong font-medium hover:underline">Request a new code</Link>
        </p>
      </Card>
    </div>
  );
}
